// =============================================================================
// STRIPE UTILITIES
// =============================================================================

import { CONFIG, ENVIRONMENT_NAME, getCurrentChannel } from '../config';

// Cache for payment links to avoid repeated API calls
const paymentLinkCache = new Map();

// Payment link fetching utilities

/**
 * Fetches payment link from API for specific channel
 * @param {string} channel - Channel name
 * @returns {Promise<string|null>} Payment link or null if not found
 */
const fetchPaymentLinkFromAPI = async (channel) => {
  try {
    // Check cache first
    const cacheKey = `paymentlink_${channel}`;
    if (paymentLinkCache.has(cacheKey)) {
      return paymentLinkCache.get(cacheKey);
    }

    const headers = {
      'Content-Type': 'application/json',
      'X-Channel': channel,
      'X-Environment': ENVIRONMENT_NAME,
    };

    // Standard headers for API requests

    const response = await fetch(`${CONFIG.api.baseUrl}/api/payment_link?channel=${channel}`, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      const paymentLink = data.payment_link;
      
      if (paymentLink) {
        // Cache the result for 5 minutes
        paymentLinkCache.set(cacheKey, paymentLink);
        setTimeout(() => paymentLinkCache.delete(cacheKey), 5 * 60 * 1000);
        
        // No console logging in production builds
        
        return paymentLink;
      }
    }

    // No console logging in production builds
    
    return null;
  } catch (error) {
    console.error('Error fetching payment link from API:', error);
    return null;
  }
};

/**
 * Gets payment link for current channel (API only)
 * @param {string} channel - Optional channel override
 * @returns {Promise<string|null>} Payment link from API or null if not available
 */
export const getPaymentLink = async (channel = null) => {
  const currentChannel = channel || getCurrentChannel();
  
  try {
    // Get from API only
    const apiPaymentLink = await fetchPaymentLinkFromAPI(currentChannel);
    if (apiPaymentLink) {
      return apiPaymentLink;
    }
  } catch (error) {
    console.error('API payment link fetch failed:', error);
  }
  
  // No fallback - return null if API doesn't provide payment link
  // No console logging in production builds
  
  return null;
};

/**
 * Redirects to Stripe checkout with proper error handling
 * @param {string} portraitUUID - The portrait UUID for client reference
 * @param {Function} onError - Error callback function
 */
export const redirectToStripeCheckout = async (portraitUUID, onError = null) => {
  try {
    // Check if Stripe is enabled
    if (!CONFIG.stripe.enabled) {
      console.warn('Stripe checkout is disabled in current environment');
      if (onError) onError(new Error('Payment system is currently disabled'));
      return;
    }

    // Get payment link from API (with fallback to env)
    const paymentLink = await getPaymentLink();
    
    if (!paymentLink) {
      const error = new Error('No payment link available');
      console.error('Stripe Error:', error.message);
      if (onError) onError(error);
      return;
    }

    // Redirect directly to Stripe payment link (no client_reference_id)
    window.location.href = paymentLink;

  } catch (error) {
    console.error('Stripe Redirect Error:', error);
    if (onError) onError(error);
  }
};

/**
 * Validates Stripe configuration for current environment
 * @returns {Promise<Object>} Validation result with status and message
 */
export const validateStripeConfig = async () => {
  const issues = [];
  
  try {
    const paymentLink = await getPaymentLink();
    
    if (!paymentLink) {
      issues.push('No payment link available from API');
    }

    if (!CONFIG.stripe.enabled) {
      issues.push('Stripe is disabled in current environment');
    }

    return {
      isValid: issues.length === 0,
      issues,
      environment: ENVIRONMENT_NAME,
      paymentLink,
      source: paymentLinkCache.has(`paymentlink_${getCurrentChannel()}`) ? 'API' : 'Not Available'
    };
  } catch (error) {
    return {
      isValid: false,
      issues: ['Error validating Stripe configuration: ' + error.message],
      environment: ENVIRONMENT_NAME,
      paymentLink: null,
      source: 'Error'
    };
  }
};

/**
 * Gets display information about current Stripe configuration
 * @returns {Promise<Object>} Stripe configuration info
 */
export const getStripeInfo = async () => {
  const validation = await validateStripeConfig();
  const paymentLink = validation.paymentLink;
  
  return {
    ...validation,
    isTestMode: paymentLink && paymentLink.includes('/test_'),
    isLiveMode: paymentLink && !paymentLink.includes('/test_'),
    enabled: CONFIG.stripe.enabled,
    channel: getCurrentChannel()
  };
};

/**
 * Debug function to test payment link API
 * @param {string} channel - Optional channel to test
 */
export const testPaymentLinkAPI = async (channel = null) => {
  const testChannel = channel || getCurrentChannel();
  // No console logging in production builds
  
  try {
    const apiLink = await fetchPaymentLinkFromAPI(testChannel);
    
    // No console logging in production builds
    
    return {
      channel: testChannel,
      apiLink,
      finalChoice: apiLink
    };
  } catch (error) {
    console.error('[STRIPE DEBUG] Error:', error);
    return { error: error.message };
  }
};
